import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getCertificateDownload,
  getLearnerTrack,
  getResourceDownload,
  submitFeedback,
  type LearnerTrack,
} from "../api";
import { Card, Shell } from "../ui";
import { RichText } from "../richText";

const COLLAPSE_STORAGE_PREFIX = "wlh.trackCollapse.";

interface StoredCollapseState {
  description: boolean;
  modules: string[];
}

// Best-effort: a private window, disabled storage, or a full quota should
// just mean the toggle doesn't survive a refresh, never a broken page.
function loadCollapseState(trackId: string): StoredCollapseState | null {
  try {
    const raw = localStorage.getItem(COLLAPSE_STORAGE_PREFIX + trackId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredCollapseState>;
    if (typeof parsed.description !== "boolean" || !Array.isArray(parsed.modules)) return null;
    return { description: parsed.description, modules: parsed.modules };
  } catch {
    return null;
  }
}

function saveCollapseState(trackId: string, state: StoredCollapseState) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_PREFIX + trackId, JSON.stringify(state));
  } catch {
    // ignore — see loadCollapseState
  }
}

const ENROLLMENT_STATE_LABEL: Record<string, string> = {
  ASSIGNED: "Not started",
  BLOCKED_PREREQ: "Locked — complete the prerequisite track first",
  IN_PROGRESS: "In progress",
  LEARNING_COMPLETE: "Learning complete — assessment due",
  ASSESSMENT_DUE: "Assessment due",
  REMEDIATION_REQUIRED: "Needs retry",
  PASSED: "Passed",
  CERTIFIED: "Certified",
  WITHDRAWN: "Withdrawn",
};

export function TrackView() {
  const { trackId } = useParams<{ trackId: string }>();
  const nav = useNavigate();
  const [track, setTrack] = useState<LearnerTrack | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingItemId, setDownloadingItemId] = useState<string | null>(null);
  const [resourceError, setResourceError] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const [collapsedModules, setCollapsedModules] = useState<Set<string>>(new Set());
  const [initialisedCollapse, setInitialisedCollapse] = useState(false);
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(false);
  const [feedbackAnswers, setFeedbackAnswers] = useState<Record<string, string>>({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackSkipped, setFeedbackSkipped] = useState(false);

  function setFeedbackAnswer(questionId: string, value: string) {
    setFeedbackAnswers((prev) => ({ ...prev, [questionId]: value }));
  }

  async function submitFeedbackForm() {
    if (!trackId || !track?.feedback) return;
    setFeedbackSubmitting(true);
    setFeedbackError(null);
    try {
      const answers = track.feedback.questions
        .map((q) => ({ questionId: q.id, value: (feedbackAnswers[q.id] ?? "").trim() }))
        .filter((a) => a.value.length > 0);
      await submitFeedback(trackId, track.feedback.templateId, answers);
      setFeedbackDone(true);
    } catch (e) {
      setFeedbackError(e instanceof Error ? e.message : String(e));
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  function toggleModule(moduleId: string) {
    setCollapsedModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId);
      else next.add(moduleId);
      return next;
    });
  }

  async function downloadCertificate() {
    if (!trackId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const { url } = await getCertificateDownload(trackId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  }

  async function downloadResource(itemId: string) {
    setDownloadingItemId(itemId);
    setResourceError(null);
    try {
      const { url } = await getResourceDownload(itemId);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      setResourceError(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloadingItemId(null);
    }
  }

  useEffect(() => {
    if (!trackId) return;
    setTrack(null);
    setInitialisedCollapse(false);
    getLearnerTrack(trackId)
      .then(setTrack)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [trackId]);

  // Restore the learner's own collapse choices from a previous visit to this
  // track (survives navigating away and back, and a page refresh). Only
  // falls back to the completed-modules-start-closed default when nothing
  // was saved yet. Only runs once per load — toggles afterward are saved,
  // not re-derived.
  useEffect(() => {
    if (!track || !trackId || initialisedCollapse) return;
    const stored = loadCollapseState(trackId);
    if (stored) {
      setCollapsedModules(new Set(stored.modules));
      setDescriptionCollapsed(stored.description);
    } else {
      setCollapsedModules(
        new Set(track.modules.filter((m) => m.state === "COMPLETED").map((m) => m.id)),
      );
    }
    setInitialisedCollapse(true);
  }, [track, trackId, initialisedCollapse]);

  useEffect(() => {
    if (!trackId || !initialisedCollapse) return;
    saveCollapseState(trackId, {
      description: descriptionCollapsed,
      modules: [...collapsedModules],
    });
  }, [trackId, initialisedCollapse, collapsedModules, descriptionCollapsed]);

  if (error)
    return (
      <Shell title="Track" nav={<Link to="/">← My Learning</Link>}>
        <Card>Could not load: {error}</Card>
      </Shell>
    );
  if (!track) return <Shell title="Loading…">{null}</Shell>;

  // Gate the modules/assessment/certificate sections behind an explicit "Start
  // Program" click for a learner who hasn't opened anything yet (Anne,
  // 2026-09-11) — the click-through page should read as a summary first, not
  // dump the full course structure immediately. Once the enrolment has moved
  // past ASSIGNED (the learner opened real content), always show the full
  // page — the gate is only for the very first visit.
  const notYetStarted = track.enrollmentState === "ASSIGNED";
  const showContent = started || !notYetStarted;

  return (
    <Shell
      title={track.programmeTitle}
      subtitle={`${track.trackTitle} · Status: ${ENROLLMENT_STATE_LABEL[track.enrollmentState] ?? track.enrollmentState}`}
      nav={<Link to="/">← My Learning</Link>}
    >
      {track.coverImageUrl && (
        <img
          src={track.coverImageUrl}
          alt=""
          style={{
            width: "100%",
            maxHeight: 220,
            objectFit: "cover",
            borderRadius: 10,
            marginTop: "1rem",
            display: "block",
          }}
        />
      )}
      {track.programmeDescription && (
        <Card>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              cursor: "pointer",
            }}
            onClick={() => setDescriptionCollapsed((c) => !c)}
          >
            <strong>
              <span style={{ display: "inline-block", width: "1.1em", color: "#888" }}>
                {descriptionCollapsed ? "▸" : "▾"}
              </span>
              About this programme
            </strong>
          </div>
          {!descriptionCollapsed && (
            <div style={{ marginTop: "0.5rem" }}>
              <RichText text={track.programmeDescription} />
            </div>
          )}
        </Card>
      )}
      {!showContent && (
        <Card>
          <button
            onClick={() => {
              if (track.diagnostic?.pre && !track.diagnostic.pre.completed) {
                nav(`/learn/diagnostics/${trackId}/pre`);
              } else {
                setStarted(true);
              }
            }}
            style={{
              padding: "0.6rem 1.3rem",
              background: "#1a1a2e",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: "0.95rem",
              fontWeight: 600,
            }}
          >
            Start program
          </button>
        </Card>
      )}
      {showContent && resourceError && <Card>Could not prepare the download: {resourceError}</Card>}
      {showContent &&
        track.modules.map((m, i) => {
          const collapsed = collapsedModules.has(m.id);
          const collapsible = m.unlocked && m.learningItems.length > 0;
          return (
            <Card key={m.id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  cursor: collapsible ? "pointer" : "default",
                }}
                onClick={collapsible ? () => toggleModule(m.id) : undefined}
              >
                <strong>
                  {collapsible && (
                    <span style={{ display: "inline-block", width: "1.1em", color: "#888" }}>
                      {collapsed ? "▸" : "▾"}
                    </span>
                  )}
                  {i + 1}. {m.title}
                  {!m.required && (
                    <span style={{ color: "#888", fontWeight: 400 }}> (optional)</span>
                  )}
                </strong>
                <span
                  style={{
                    fontSize: "0.85rem",
                    color: m.state === "COMPLETED" ? "#0a7d33" : "#666",
                  }}
                >
                  {m.state === "COMPLETED" ? "Completed" : m.unlocked ? "Available" : "Locked"}
                </span>
              </div>
              {m.unlocked && !collapsed && m.learningItems.length > 0 && (
                <ul style={{ marginTop: "0.5rem", marginBottom: 0 }}>
                  {m.learningItems.map((li) => (
                    <li key={li.id}>
                      {li.type === "HTML_PACKAGE" ? (
                        <Link to={`/learn/items/${li.id}`}>{li.title}</Link>
                      ) : li.type === "EXTERNAL_LINK" && li.externalUrl ? (
                        <a href={li.externalUrl} target="_blank" rel="noreferrer">
                          {li.title} ↗
                        </a>
                      ) : li.type === "DOWNLOADABLE_RESOURCE" && li.downloadAllowed ? (
                        <>
                          {li.title}{" "}
                          <button
                            onClick={() => downloadResource(li.id)}
                            disabled={downloadingItemId === li.id}
                            style={{
                              border: "none",
                              background: "none",
                              color: "#1a1a2e",
                              textDecoration: "underline",
                              cursor: downloadingItemId === li.id ? "default" : "pointer",
                              font: "inherit",
                              padding: 0,
                            }}
                          >
                            {downloadingItemId === li.id ? "Preparing…" : "Download"}
                          </button>
                        </>
                      ) : (
                        li.title
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          );
        })}

      {showContent && track.assessment && (
        <Card>
          <strong>Final assessment</strong>
          <p style={{ color: "#666", fontSize: "0.9rem" }}>
            {track.assessment.attemptsUsed} of {track.assessment.maxAttempts} attempt(s) used
            {track.assessment.lastOutcome && ` · last attempt: ${track.assessment.lastOutcome}`}.
          </p>
          {track.assessment.eligible ? (
            <Link
              to={
                track.diagnostic?.post && !track.diagnostic.post.completed
                  ? `/learn/diagnostics/${trackId}/post`
                  : `/learn/assessments/${trackId}`
              }
              style={{
                display: "inline-block",
                padding: "0.5rem 1rem",
                background: "#1a1a2e",
                color: "#fff",
                borderRadius: 8,
                textDecoration: "none",
                fontSize: "0.9rem",
              }}
            >
              {track.assessment.attemptsUsed > 0 ? "Retry assessment" : "Take assessment"}
            </Link>
          ) : (
            <p style={{ color: "#888" }}>Complete all required modules above first.</p>
          )}
        </Card>
      )}

      {track.credential && (
        <Card>
          <strong>Certificate</strong>
          {track.credential.state === "ISSUED" && (
            <div style={{ marginTop: "0.5rem" }}>
              <p style={{ color: "#666", fontSize: "0.9rem" }}>
                Credential ID: {track.credential.credentialId}
              </p>
              <button
                onClick={downloadCertificate}
                disabled={downloading}
                style={{
                  padding: "0.5rem 1rem",
                  background: "#1a1a2e",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  cursor: downloading ? "default" : "pointer",
                  fontSize: "0.9rem",
                }}
              >
                {downloading ? "Preparing…" : "Download certificate"}
              </button>
              {downloadError && (
                <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{downloadError}</p>
              )}
            </div>
          )}
          {track.credential.state === "PENDING" && (
            <p style={{ color: "#888" }}>
              Your certificate is being generated — check back shortly.
            </p>
          )}
          {track.credential.state === "ERROR" && (
            <p style={{ color: "#b00020" }}>
              There was an issue generating your certificate. Your completion is unaffected — please
              contact your administrator.
            </p>
          )}
        </Card>
      )}

      {track.feedback && !feedbackSkipped && !feedbackDone && (
        <Card>
          <strong>Quick feedback</strong>
          <p style={{ color: "#666", fontSize: "0.9rem" }}>
            Optional — helps us improve this programme.
          </p>
          {track.feedback.questions.map((q) => (
            <div key={q.id} style={{ marginBottom: "0.9rem" }}>
              <p style={{ fontWeight: 600, marginBottom: "0.3rem" }}>{q.text}</p>
              {q.type === "RATING_5" && (
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <label
                      key={n}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        fontSize: "0.8rem",
                      }}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={feedbackAnswers[q.id] === String(n)}
                        onChange={() => setFeedbackAnswer(q.id, String(n))}
                      />
                      {n}
                    </label>
                  ))}
                </div>
              )}
              {q.type === "NPS_0_10" && (
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  {Array.from({ length: 11 }, (_, n) => n).map((n) => (
                    <label
                      key={n}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        fontSize: "0.75rem",
                      }}
                    >
                      <input
                        type="radio"
                        name={q.id}
                        checked={feedbackAnswers[q.id] === String(n)}
                        onChange={() => setFeedbackAnswer(q.id, String(n))}
                      />
                      {n}
                    </label>
                  ))}
                </div>
              )}
              {q.type === "TEXT" && (
                <textarea
                  value={feedbackAnswers[q.id] ?? ""}
                  onChange={(e) => setFeedbackAnswer(q.id, e.target.value)}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "0.4rem 0.5rem",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              )}
              {q.type === "SINGLE_CHOICE" && (
                <div>
                  {(q.options ?? []).map((opt) => (
                    <label key={opt} style={{ display: "block", padding: "0.15rem 0" }}>
                      <input
                        type="radio"
                        name={q.id}
                        checked={feedbackAnswers[q.id] === opt}
                        onChange={() => setFeedbackAnswer(q.id, opt)}
                      />{" "}
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
          {feedbackError && (
            <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{feedbackError}</p>
          )}
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
            <button
              type="button"
              onClick={submitFeedbackForm}
              disabled={feedbackSubmitting}
              style={{
                padding: "0.5rem 1rem",
                background: "#1a1a2e",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: feedbackSubmitting ? "default" : "pointer",
                fontSize: "0.9rem",
              }}
            >
              {feedbackSubmitting ? "Submitting…" : "Submit feedback"}
            </button>
            <button
              type="button"
              onClick={() => setFeedbackSkipped(true)}
              style={{
                border: "none",
                background: "none",
                color: "#666",
                textDecoration: "underline",
                cursor: "pointer",
                font: "inherit",
              }}
            >
              Skip
            </button>
          </div>
        </Card>
      )}
      {track.feedback && feedbackDone && (
        <Card>
          <p style={{ color: "#0a7d33", margin: 0 }}>Thanks for your feedback!</p>
        </Card>
      )}
    </Shell>
  );
}
